import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";

const STATUSES = ["Open","In Progress","Waiting for Candidate","Resolved","Closed"];
const PRIORITIES = ["Low","Normal","High","Urgent"];

export default function AdminSupport() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);

  const load = () => {
    const params = status !== "all" ? { status } : {};
    api.get("/admin/support/tickets", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { load(); }, [status]);

  const openT = async (t) => {
    const { data } = await api.get(`/support/tickets/${t.id}`);
    setSelected(data);
  };
  const update = async (patch) => {
    try {
      await api.put(`/admin/support/tickets/${selected.id}`, patch);
      toast.success("Updated");
      openT(selected);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const send = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${selected.id}/replies`, { message: reply, internal });
      setReply(""); setInternal(false); openT(selected);
      toast.success("Reply sent");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-white flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-[#F59E0B]"/>Support Center</h1>
      <p className="text-slate-400 text-sm mt-1">Manage candidate support tickets. Internal notes are never shown to candidates.</p>

      <div className="mt-6 flex gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52 bg-slate-800 border-slate-700 text-white" data-testid="support-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>{["all",...STATUSES].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card className="bg-slate-900 border-slate-800 p-4">
          {rows.length === 0 ? <p className="p-6 text-slate-400 text-center">No tickets in this filter.</p> :
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {rows.map(t => (
              <button key={t.id} onClick={()=>openT(t)} className={`w-full text-left p-3 rounded-md border transition-colors ${selected?.id===t.id ? "border-[#F59E0B] bg-slate-800/50" : "border-slate-800 hover:bg-slate-800/30"}`} data-testid={`admin-ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{t.subject}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{t.candidate_name} · {t.category}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`border-none ${t.priority === "Urgent" ? "bg-red-900/40 text-red-300" : t.priority === "High" ? "bg-amber-900/40 text-amber-300" : "bg-slate-800 text-slate-300"}`}>{t.priority}</Badge>
                    <Badge className="bg-slate-800 border-slate-700 text-slate-200">{t.status}</Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>}
        </Card>

        <Card className="bg-slate-900 border-slate-800 p-6">
          {!selected ? <p className="text-slate-400 text-sm">Select a ticket to view details and reply.</p> : <>
            <div className="text-white font-semibold text-lg">{selected.subject}</div>
            <div className="text-xs text-slate-400 mt-1">{selected.id} · {selected.candidate_name} ({selected.candidate_email})</div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <Select value={selected.status} onValueChange={v=>update({status: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="admin-ticket-status"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={selected.priority} onValueChange={v=>update({priority: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue/></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-xs text-slate-400 flex items-center px-2">Assigned: {selected.assigned_name || "—"}</div>
            </div>
            <div className="mt-4 max-h-72 overflow-y-auto space-y-2">
              <div className="rounded-md p-3 bg-slate-800/40 border border-slate-800">
                <div className="text-xs text-slate-400">{selected.candidate_name} · {new Date(selected.created_at).toLocaleString()}</div>
                <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{selected.description}</p>
              </div>
              {(selected.replies || []).map(r => (
                <div key={r.id} className={`rounded-md p-3 ${r.internal ? "bg-amber-900/20 border border-amber-800" : r.author_role === "candidate" ? "bg-blue-900/20 border border-blue-800" : "bg-emerald-900/20 border border-emerald-800"}`}>
                  <div className="text-xs text-slate-400">{r.author_name} ({r.author_role}){r.internal ? " · INTERNAL" : ""} · {new Date(r.created_at).toLocaleString()}</div>
                  <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{r.message}</p>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Textarea rows={3} value={reply} onChange={e=>setReply(e.target.value)} placeholder="Reply to the candidate…" className="bg-slate-800 border-slate-700 text-white" data-testid="admin-reply-input"/>
              <div className="flex items-center justify-between mt-2">
                <label className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={internal} onChange={e=>setInternal(e.target.checked)}/> Internal note (hidden from candidate)
                </label>
                <Button onClick={send} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="admin-reply-send">Send Reply</Button>
              </div>
            </div>
          </>}
        </Card>
      </div>
    </AdminLayout>
  );
}
